import modernizr from 'modernizr'

import { addEventListener } from 'j/main/lib/tools/events'

export const EXITED = 'exited'
export const ENTERING = 'entering'
export const ENTERED = 'entered'
export const EXITING = 'exiting'

const noop = () => {}

const addClass = (node, classes) =>
  node && classes && classes.split(' ').forEach(c => node.classList.add(c))

const removeClass = (node, classes) =>
  node && classes && classes.split(' ').forEach(c => node.classList.remove(c))

/**
 * Gets the properly prefixed transitionend event name.
 * Primarily used because the alternative is to specify multiple
 * event names which results in the handler being called more than
 * once in some browsers.
 *
 * @function transitionend
 * @returns {string}
 */
const transitionend = (function (transition) {
  const transEndEventNames = {
    // Saf 6, Android Browser
    WebkitTransition: 'webkitTransitionEnd',
    // only for FF < 15
    MozTransition: 'transitionend',
    // IE10, Opera, Chrome, FF 15+, Saf 7+
    transition: 'transitionend'
  }

  return transEndEventNames[transition]
})(modernizr.prefixed('transition'))

export default class Transition {
  constructor (conf = {}) {
    this.classNames = conf.classNames
    this.node = conf.node
    this.callbacks = {}
    this.timeout = conf.timeout ? conf.timeout : 5000
    this.callbacks.onEnter = conf.onEnter ? conf.onEnter : noop
    this.callbacks.onEntering = conf.onEntering ? conf.onEntering : noop
    this.callbacks.onEntered = conf.onEntered ? conf.onEntered : noop
    this.callbacks.onExit = conf.onExit ? conf.onExit : noop
    this.callbacks.onExiting = conf.onExiting ? conf.onExiting : noop
    this.callbacks.onExited = conf.onExited ? conf.onExited : noop
  }

  init = nextStatus => {
    const promise = new Promise((resolve, reject) => {
      if (nextStatus === ENTERING) {
        this.performEnter(this.node, resolve)
      } else {
        this.performExit(this.node, resolve)
      }
    })
    return promise
  }

  performEnter (node, done) {
    this.onEnter(node)

    this.onEntering(node)

    this.onTransitionEnd(node, () => {
      this.onEntered(node)
      done()
    })
  }

  performExit (node, done) {
    this.onExit(node)

    this.onExiting(node)

    this.onTransitionEnd(node, () => {
      this.onExited(node)
      done()
    })
  }

  setNextCallback (callback) {
    let active = true

    this.nextCallback = event => {
      if (active) {
        active = false
        this.nextCallback = null
        callback(event)
      }
    }

    this.nextCallback.cancel = () => {
      active = false
    }

    return this.nextCallback
  }

  addEndListener (node, done) {
    addEventListener(
      node,
      transitionend,
      (e) => {
        e.stopPropagation()
        done()
      },
      { once: true }
    )
  }

  onTransitionEnd (node, handler) {
    this.setNextCallback(handler)
    if (node) {
      if (this.addEndListener) {
        this.addEndListener(node, this.nextCallback)
      }
      if (this.timeout != null) {
        setTimeout(this.nextCallback, this.timeout)
      }
    } else {
      setTimeout(this.nextCallback, 0)
    }
  }

  onEnter = node => {
    const { className } = this.getClassNames('enter')

    this.removeClasses(node, 'exit')
    addClass(node, className)

    if (this.callbacks.onEnter) {
      this.callbacks.onEnter(node)
    }
  }

  onEntering = node => {
    const { activeClassName } = this.getClassNames('enter')

    this.reflowAndAddClass(node, activeClassName)

    if (this.callbacks.onEntering) {
      this.callbacks.onEntering(node)
    }
  }

  onEntered = node => {
    const { doneClassName } = this.getClassNames('enter')

    this.removeClasses(node, 'enter')
    addClass(node, doneClassName)

    if (this.callbacks.onEntered) {
      this.callbacks.onEntered(node)
    }
  }

  onExit = node => {
    const { className } = this.getClassNames('exit')

    this.removeClasses(node, 'enter')
    addClass(node, className)

    if (this.callbacks.onExit) {
      this.callbacks.onExit(node)
    }
  }

  onExiting = node => {
    const { activeClassName } = this.getClassNames('exit')

    this.reflowAndAddClass(node, activeClassName)

    if (this.callbacks.onExiting) {
      this.callbacks.onExiting(node)
    }
  }

  onExited = node => {
    const { doneClassName } = this.getClassNames('exit')

    this.removeClasses(node, 'exit')
    addClass(node, doneClassName)

    if (this.callbacks.onExited) {
      this.callbacks.onExited(node)
    }
  }

  getClassNames = type => {
    const { classNames } = this

    let className =
      typeof classNames !== 'string'
        ? classNames[type]
        : classNames + '-' + type

    let activeClassName =
      typeof classNames !== 'string'
        ? classNames[type + 'Active']
        : className + '-active'

    let doneClassName =
      typeof classNames !== 'string'
        ? classNames[type + 'Done']
        : className + '-done'

    return {
      className,
      activeClassName,
      doneClassName
    }
  }

  removeClasses (node, type) {
    const { className, activeClassName, doneClassName } = this.getClassNames(
      type
    )
    className && removeClass(node, className)
    activeClassName && removeClass(node, activeClassName)
    doneClassName && removeClass(node, doneClassName)
  }

  cleanUp () {
    this.removeClasses(this.node, 'exit')
    this.removeClasses(this.node, 'enter')
  }

  reflowAndAddClass (node, className) {
    // This is for to force a repaint,
    // which is necessary in order to transition styles when adding a class name.
    /* eslint-disable no-unused-expressions */
    node && node.scrollTop
    /* eslint-enable no-unused-expressions */
    addClass(node, className)
  }
}
